import { useState, useMemo } from 'react'
import { useAuth, hasPermission } from '@/contexts/AuthContext'
import { useAuditLogs, useUserActivitySummary } from '@/hooks/useAuditLogs'
import { formatDateTime } from '@/lib/utils'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { StatsCard } from '@/components/dashboard/StatsCard'
import { FileText, Loader2, LogIn, Users, Activity, ChevronLeft, ChevronRight } from 'lucide-react'
import type { AdminRole } from '@/types/database'

const ROLE_LABELS: Record<AdminRole, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  viewer: 'Viewer',
  investor: 'Investor',
}

function getRoleBadgeColor(role: string) {
  switch (role) {
    case 'super_admin':
      return 'bg-brand-fg/20 text-brand-fg'
    case 'admin':
      return 'bg-blue-500/20 text-blue-400'
    case 'investor':
      return 'bg-emerald-500/20 text-emerald-400'
    default:
      return 'bg-muted text-muted-foreground'
  }
}

function getActionBadgeColor(action: string) {
  switch (action) {
    case 'login_success':
      return 'bg-emerald-500/20 text-emerald-400'
    case 'logout':
      return 'bg-slate-500/20 text-slate-400'
    case 'mfa_setup_complete':
      return 'bg-blue-500/20 text-blue-400'
    case 'mfa_disabled':
      return 'bg-amber-500/20 text-amber-400'
    case 'user_role_updated':
      return 'bg-purple-500/20 text-purple-400'
    case 'user_deleted':
      return 'bg-red-500/20 text-red-400'
    default:
      return 'bg-muted text-muted-foreground'
  }
}

function formatActionLabel(action: string): string {
  return action
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

function formatDetails(details: Record<string, unknown> | null): string {
  if (!details || Object.keys(details).length === 0) return ''
  const parts: string[] = []
  for (const [key, value] of Object.entries(details)) {
    const label = key.replace(/_/g, ' ')
    parts.push(`${label}: ${String(value)}`)
  }
  return parts.join(', ')
}

function shortenUserAgent(ua: string | null): string {
  if (!ua) return ''
  if (ua.includes('Firefox')) return 'Firefox'
  if (ua.includes('Edg/')) return 'Edge'
  if (ua.includes('Chrome')) return 'Chrome'
  if (ua.includes('Safari')) return 'Safari'
  return 'Other'
}

const FILTER_OPTIONS = [
  { value: '', label: 'All Events' },
  { value: 'sign_ins', label: 'Sign Ins' },
  { value: 'sign_outs', label: 'Sign Outs' },
  { value: 'mfa_setup', label: 'MFA Setup' },
  { value: 'role_changes', label: 'Role Changes' },
  { value: 'user_deletions', label: 'User Deletions' },
]

export function AuditPage() {
  const { adminUser } = useAuth()
  const isSuperAdmin = hasPermission(adminUser?.role, 'super_admin') && adminUser?.role === 'super_admin'

  const [actionFilter, setActionFilter] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [page, setPage] = useState(0)
  const pageSize = 20

  const { data: logs, isLoading: logsLoading, error: logsError } = useAuditLogs({
    actionFilter: actionFilter || undefined,
    limit: 500, // fetch larger set for client-side pagination
  })
  const { data: userActivity, isLoading: activityLoading } = useUserActivitySummary()

  // Date-filtered logs
  const filteredLogs = useMemo(() => {
    if (!logs) return []
    let filtered = logs
    if (dateFrom) {
      const from = new Date(dateFrom)
      filtered = filtered.filter((l) => new Date(l.created_at) >= from)
    }
    if (dateTo) {
      const to = new Date(dateTo + 'T23:59:59')
      filtered = filtered.filter((l) => new Date(l.created_at) <= to)
    }
    return filtered
  }, [logs, dateFrom, dateTo])

  const totalFilteredPages = Math.max(1, Math.ceil(filteredLogs.length / pageSize))
  const pageLogs = filteredLogs.slice(page * pageSize, (page + 1) * pageSize)

  const totalSignIns = userActivity?.reduce((sum, u) => sum + u.sign_in_count, 0) ?? 0
  const activeUsers24h = userActivity?.filter((u) => {
    if (!u.last_action_at) return false
    const diff = Date.now() - new Date(u.last_action_at).getTime()
    return diff < 24 * 60 * 60 * 1000
  }).length ?? 0
  const totalEvents = userActivity?.reduce((sum, u) => sum + u.total_actions, 0) ?? 0

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold">Audit Logs</h2>
        <p className="text-sm sm:text-base text-muted-foreground">View admin activity and security events</p>
      </div>

      {/* User Activity Summary: super_admin only */}
      {isSuperAdmin && (
        <>
          <div className="grid gap-3 sm:gap-4 grid-cols-2 sm:grid-cols-3">
            <StatsCard
              title="Total Sign-ins"
              value={totalSignIns}
              icon={LogIn}
              loading={activityLoading}
            />
            <StatsCard
              title="Active Users (24h)"
              value={activeUsers24h}
              icon={Users}
              loading={activityLoading}
            />
            <StatsCard
              title="Total Audit Events"
              value={totalEvents}
              icon={Activity}
              loading={activityLoading}
            />
          </div>

          <Card>
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <Users className="h-5 w-5" />
                Per-User Metrics
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 sm:p-0">
              {activityLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[780px]">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="px-4 py-3 text-left font-medium text-muted-foreground">Email</th>
                        <th className="px-4 py-3 text-left font-medium text-muted-foreground">Role</th>
                        <th className="px-4 py-3 text-left font-medium text-muted-foreground">Sign-ins</th>
                        <th className="px-4 py-3 text-left font-medium text-muted-foreground">Last Sign In</th>
                        <th className="px-4 py-3 text-left font-medium text-muted-foreground">Last Action</th>
                        <th className="px-4 py-3 text-left font-medium text-muted-foreground">Total Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userActivity?.map((u) => (
                        <tr key={u.id} className="border-b border-border last:border-0 hover:bg-muted/50">
                          <td className="px-4 py-3 font-medium">{u.email}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getRoleBadgeColor(u.role)}`}>
                              {ROLE_LABELS[u.role as AdminRole] || u.role}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">{u.sign_in_count}</td>
                          <td className="px-4 py-3 text-muted-foreground text-xs whitespace-nowrap">
                            {u.last_sign_in ? formatDateTime(u.last_sign_in) : 'Never'}
                          </td>
                          <td className="px-4 py-3 text-muted-foreground text-xs whitespace-nowrap">
                            {u.last_action ? (
                              <span>
                                {formatActionLabel(u.last_action)}
                                {u.last_action_at ? ` (${formatDateTime(u.last_action_at)})` : ''}
                              </span>
                            ) : (
                              'None'
                            )}
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">{u.total_actions}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Activity Log: admin + super_admin */}
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <FileText className="h-5 w-5" />
              Activity Log
            </CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setPage(0) }}
                className="h-8 px-2 rounded-md border border-input bg-background text-sm"
                placeholder="From"
              />
              <input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setPage(0) }}
                className="h-8 px-2 rounded-md border border-input bg-background text-sm"
                placeholder="To"
              />
              <select
                value={actionFilter}
                onChange={(e) => {
                  setActionFilter(e.target.value)
                  setPage(0)
                }}
                className="h-8 px-2 rounded-md border border-input bg-background text-sm"
              >
                {FILTER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0 sm:p-0">
          {logsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : logsError ? (
            <div className="py-8 text-center text-muted-foreground px-4">
              <p className="text-sm">{logsError instanceof Error ? logsError.message : 'Failed to load audit logs'}</p>
            </div>
          ) : !logs || logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <FileText className="h-10 w-10 mb-4 opacity-50" />
              <p className="text-sm">No events found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[720px]">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="px-4 py-3 text-left font-medium text-muted-foreground">Time</th>
                      <th className="px-4 py-3 text-left font-medium text-muted-foreground">User</th>
                      <th className="px-4 py-3 text-left font-medium text-muted-foreground">Action</th>
                      <th className="px-4 py-3 text-left font-medium text-muted-foreground">Details</th>
                      <th className="px-4 py-3 text-left font-medium text-muted-foreground">Browser</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageLogs.map((log) => (
                      <tr key={log.id} className="border-b border-border last:border-0 hover:bg-muted/50">
                        <td className="px-4 py-3 text-muted-foreground text-xs whitespace-nowrap">
                          {formatDateTime(log.created_at)}
                        </td>
                        <td className="px-4 py-3 font-medium text-xs">
                          {log.admin_users?.email || 'Unknown'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getActionBadgeColor(log.action)}`}>
                            {formatActionLabel(log.action)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground text-xs max-w-[300px] truncate">
                          {formatDetails(log.details)}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground text-xs">
                          {shortenUserAgent(log.user_agent)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredLogs.length > pageSize && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                  <span className="text-xs text-muted-foreground">
                    {page * pageSize + 1}-{Math.min((page + 1) * pageSize, filteredLogs.length)} of {filteredLogs.length}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      disabled={page === 0}
                      className="rounded-lg border border-border px-2 py-1 text-sm disabled:opacity-50 hover:bg-muted"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <span className="text-xs text-muted-foreground">
                      Page {page + 1} of {totalFilteredPages}
                    </span>
                    <button
                      onClick={() => setPage((p) => Math.min(totalFilteredPages - 1, p + 1))}
                      disabled={page >= totalFilteredPages - 1}
                      className="rounded-lg border border-border px-2 py-1 text-sm disabled:opacity-50 hover:bg-muted"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
